import { addSplitBlock, sheetAmount, splitBlock } from './splits';
import type { OverviewDto, TransactionDto } from '../../../shared/api';
import { dayOf, esc, isoDay, money } from '../format';
import { itemPicker, spendingCategories } from './items';
import { cardName } from './activity';
import { centsField } from '../cents';
import { reasonFor } from './newcard';
import { canTip, sheetTipBlock, tipProminent, tipSheetAmount } from './tips';
import { canFund, sheetFundBlock } from './savings';
import { noteField } from './notes';
import { ADD_INTO } from '../../../shared/months';

/**
 * The purchase's amount, cents first: "Bank charge" with a tip, "Full amount (the bank charge)" on
 * a split I sent, else "Amount". The edit sheet's field, also on a New purchases row's Edit sheet
 * for an auto-logged charge (views/newsheet.ts). The server's PATCH rules apply to both.
 */
export const amountField = (t: TransactionDto | null, id: string, cents: number | null, extra = '') =>
  `<label class="fld"><span>${t?.tipCents ? 'Bank charge' : t?.split && t.split.direction === 'sent' ? 'Full amount (the bank charge)' : 'Amount'}</span><div class="amt"><b>$</b>${centsField(id, cents, extra)}</div></label>`;
import { splitsEnabled } from '../../../shared/partner';

/** "Card: Chase ...5411", "Card: Joint Visa", or "Card: ...5411" when the bank is not known. */
const cardLine = (label: string) => label.replace(/^Card \.\.\./, '...');

/**
 * The amount a new purchase starts with: the picked item's amount this month (last month's
 * actual for an item on last month's amount), else the planned amount of a category with "Same
 * amount every month", else nothing. It only ever fills an empty (or still pre-filled) field.
 */
export function prefillCents(d: OverviewDto, categoryId: string | null, itemId: string | null = null): number | null {
  const item = itemId ? d.items.find((i) => i.id === itemId && i.kind !== 'transfer') : undefined;
  // A spread item's charge is its yearly amount, never its monthly share.
  if (item) return (item.spread ? item.amountCents : item.month?.expectedCents || item.amountCents) || null;
  const k = categoryId ? d.categories.find((x) => x.id === categoryId) : undefined;
  return k?.sameAmount && k.budgetCents > 0 ? k.budgetCents : null;
}

/**
 * After a category or item pick: the amount field's new value, or null to leave it alone. An
 * amount the user typed is never replaced; an empty or still pre-filled one follows the pick.
 */
export function refillAmount(current: string, prefilled: boolean, cents: number | null): { value: string; prefilled: boolean } | null {
  if (current.trim() !== '' && !prefilled) return null;
  return cents ? { value: (cents / 100).toFixed(2), prefilled: true } : { value: '', prefilled: false };
}

/** Dollars with cents only when there are some: "$250", "$17.50". */
const plain = (c: number) => money(c, c % 100 ? 2 : 0);

/** "Logged $250 to Car payment": the undo toast after a one-tap log. */
export const loggedText = (cents: number, category: string) => `Logged ${plain(cents)} to ${category}`;

export interface LearnChoice {
  merchant: string;
  /** The category picked (null: none). */
  category: string | null;
  /** The item picked, when that is what changed. */
  item: string | null;
  /** "Use for every purchase from <merchant>". */
  remember: boolean;
  /** "Also change the N earlier purchases" (category changes only). */
  earlier: boolean;
  /** N. */
  others: number;
}

/**
 * What saving a corrected purchase will do, in one line: "Saving files this one under Groceries,
 * and future purchases from Costco too." Shown under the correction options in the edit sheet.
 */
export function learnLine(c: LearnChoice): string {
  if (!c.category) return 'Saving leaves this purchase without a category.';
  const what = c.item ? `links this one to ${c.item}` : `files this one under ${c.category}`;
  const earlier = c.earlier && c.others > 0 && !c.item ? ` and moves ${c.others === 1 ? 'the earlier purchase' : `the ${c.others} earlier purchases`}` : '';
  const future = c.remember ? `, and future purchases from ${c.merchant} too` : '';
  return `Saving ${what}${earlier}${future}.${c.remember ? '' : ' Hush will keep asking about the next one.'}`;
}

/** "Also change the 3 earlier purchases" (or "the earlier purchase"). */
export const earlierLabel = (n: number) => `Also change ${n === 1 ? 'the earlier purchase' : `the ${n} earlier purchases`}`;

/**
 * The correction options under Category, for an edited purchase whose category or item changes:
 * "Use for every purchase from <merchant>" (ticked), "Also change the N earlier purchases"
 * (unticked; shown once N is known and above 0) and the line saying what saving will do.
 * Hidden until a change; main.ts shows and fills it.
 */
export const learnBlock = (t: TransactionDto) =>
  `<div id="f-learn" class="learn" hidden>
  <label class="check"><input type="checkbox" id="f-remember" checked> Use for every purchase from ${esc(t.merchant)}</label>
  <label class="check" id="f-earlier-f" hidden><input type="checkbox" id="f-earlier"> <span id="f-earlier-label"></span></label>
  <p class="hint" id="f-learn-say"></p></div>`;

/** "How Hush filed this" in the edit sheet. */
export const howFiled = (d: OverviewDto, t: TransactionDto) =>
  `<p class="hint howfiled"><b>How Hush filed this</b><br>${esc(reasonFor(d, t))}</p>`;

export function vSheet(
  d: OverviewDto,
  t: TransactionDto | null,
  cat: string | null,
  prefill: { amountCents?: number | null; date?: string; itemId?: string } = {},
): string {
  const { key, today } = d.month;
  const date = t ? t.date : (prefill.date ?? isoDay(key, today));
  // A new purchase in a "same amount every month" category, or for an item, starts at its amount
  // (marked as pre-filled, so a later category or item pick may replace it until the user types).
  const auto = !t && prefill.amountCents == null ? prefillCents(d, cat, prefill.itemId ?? null) : null;
  const amount = t ? t.amountCents : (prefill.amountCents ?? auto);
  // The card from its alert ("Card: Chase ...5411", or its nickname). Hand-added purchases have none.
  const card = t?.cardLast4 ? d.cards.find((k) => k.last4 === t.cardLast4) : undefined;
  // A purchase files into spending only. A Done entry keeps its money set aside category.
  const choosable = t?.source === 'transfer' ? d.categories.filter((k) => k.kind === 'set_aside') : spendingCategories(d);
  // Editing: Where is the name Hush shows. The bank's own text stays underneath, and stays the
  // key Hush files by. Adding: Where is whatever the user types.
  const where = t
    ? `<label class="fld"><span>Where</span><input id="f-mer" type="text" maxlength="60" placeholder="Store or merchant" value="${esc(t.merchant)}"></label>
  ${t.bankText !== t.merchant ? `<p class="hint banktext">Bank text: ${esc(t.bankText)}</p>` : ''}
  ${card ? `<p class="hint cardline">Card: ${esc(cardLine(card.label))} <button class="linkbtn" data-a="card-rename" data-id="${esc(card.id)}">Rename card</button></p>` : t.cardLast4 ? `<p class="hint cardline">Card: ${esc(cardLine(cardName(d, t.cardLast4)!))}</p>` : ''}
  <label class="check"><input type="checkbox" id="f-every" checked> Use this name for every purchase from this merchant</label>
  ${t.customName ? '<button class="linkbtn" data-a="reset-name">Reset to original</button>' : ''}`
    : `<label class="fld"><span>Where</span><input id="f-mer" type="text" maxlength="60" placeholder="Store or merchant" value=""></label>`;
  return `<div class="grab"></div><h2>${t ? esc(t.merchant) : 'Add spending'}</h2>
  ${t ? sheetAmount(t) + tipSheetAmount(t) : ''}
  ${amountField(t, 'f-amt', amount, auto ? ' data-autofill="1"' : '')}
  ${
    canTip(d, t, cat)
      ? sheetTipBlock({ chargeCents: amount ?? null, tipCents: t?.tipCents ?? null, usuallyTipped: !!t?.usuallyTipped, prominent: tipProminent(d, t, cat) })
      : ''
  }
  ${canFund(d, t, cat) ? sheetFundBlock(d, t, t?.fundedFromCategoryId ?? '') : ''}
  ${where}
  <div class="fld"><span>Category</span><div class="chips">${choosable
    .map((k) => `<button class="chip" data-a="pick" data-cat="${esc(k.id)}" aria-pressed="${cat === k.id}">${esc(k.name)}</button>`)
    .join('')}</div></div>
  <div id="f-item-slot">${itemPicker(d, cat, t ? (t.categoryId === cat ? t.itemId : null) : (prefill.itemId ?? null))}</div>
  ${t && t.source !== 'transfer' ? learnBlock(t) : ''}
  <label class="fld"><span>Date</span><input id="f-date" type="date" min="${isoDay(key, 1)}" max="${isoDay(key, Math.max(1, today))}" value="${date}"></label>
  ${!t && !d.month.isCurrent ? `<p class="hint addmonth">${esc(ADD_INTO(key))}</p>` : ''}
  ${noteField('f-note', t?.note ?? null, '', !t && splitsEnabled(d.partner) ? { partner: d.partner.linked!.name, switchId: 'f-split' } : undefined)}
  ${t ? howFiled(d, t) : ''}
  ${t ? splitBlock(d, t) : addSplitBlock(d)}
  <p class="err" id="f-err"></p>
  <div class="btns"><button class="btn primary" data-a="save">${t ? 'Save changes' : 'Log spending'}</button>${t ? '<button class="btn danger" data-a="del">Delete</button>' : ''}</div>
  ${t && t.categoryId && !t.itemId && d.categories.find((k) => k.id === t.categoryId)?.kind !== 'set_aside' ? '<button class="linkbtn" data-a="make-item">Make this a monthly item</button>' : ''}`;
}

/** Clamp a date picked in the sheet to this month, up to today. */
export function clampDate(d: OverviewDto, value: string): string {
  const { key, today } = d.month;
  const day = value.startsWith(key) ? dayOf(value) : today;
  return isoDay(key, Math.min(today, Math.max(1, day || today)));
}
